import { h, Fragment } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

import { groupReportLines, reportGross } from '../../lib/conventionEconomy';

// One extracted payment-provider report (convention.vippsReports entry or a
// clubReport document) — shared by the stevneøkonomi drawer and Klubbdrift →
// Økonomi. Collapsed: kind badge, filename, meta line, warning count, gross,
// delete. Expanded: warnings, product lines by varegruppe, sales units, totals.

const kr = n => `${util.format(n || 0, 0)} kr`;
const krExact = n => `${util.format(n || 0, 2)} kr`;

export const KIND_LABEL = {
    sales: 'Salgsrapport',
    settlement: 'Oppgjørsrapport',
    other: 'Ukjent rapport',
};
const KIND_CLASS = {
    sales: 'text-bg-success',
    settlement: 'text-bg-primary',
    other: 'text-bg-secondary',
};

function SalesLines({ report }) {
    const groups = groupReportLines(report.lines);
    if (groups.length === 0) return null;
    return (
        <div class='table-responsive mt-2'>
            <table class='table table-sm mb-0'>
                <thead>
                    <tr class='small text-body-secondary'>
                        <th><Text id='convention.economy-col-item'>Item</Text></th>
                        <th class='text-end'><Text id='convention.economy-col-qty'>Qty</Text></th>
                        <th class='text-end'><Text id='convention.economy-col-inc-vat'>Incl. VAT</Text></th>
                        <th class='text-end d-none d-md-table-cell'><Text id='convention.economy-col-vat'>VAT</Text></th>
                        <th class='text-end d-none d-md-table-cell'><Text id='convention.economy-col-ex-vat'>Excl. VAT</Text></th>
                    </tr>
                </thead>
                <tbody>
                    {groups.map(g => (<Fragment key={`g-${g.group}`}>
                        <tr class='table-active fw-bold'>
                            <td>{g.group}</td>
                            <td class='text-end'>{g.qty}</td>
                            <td class='text-end'>{kr(g.amountIncVat)}</td>
                            <td class='text-end d-none d-md-table-cell'>{kr(g.vat)}</td>
                            <td class='text-end d-none d-md-table-cell'>{kr(g.amountExVat)}</td>
                        </tr>
                        {g.lines.map((l, idx) => (
                            <tr key={`l-${g.group}-${idx}`}>
                                <td class='ps-4'>{l.name}</td>
                                <td class='text-end'>{l.qty}</td>
                                <td class='text-end'>{l.amountIncVat !== null ? kr(l.amountIncVat) : '—'}</td>
                                <td class='text-end d-none d-md-table-cell'>{l.vat !== null ? kr(l.vat) : '—'}</td>
                                <td class='text-end d-none d-md-table-cell'>{l.amountExVat !== null ? kr(l.amountExVat) : '—'}</td>
                            </tr>
                        ))}
                    </Fragment>))}
                </tbody>
            </table>
        </div>
    );
}

function SalesUnits({ report }) {
    const units = Array.isArray(report.salesUnits) ? report.salesUnits : [];
    if (units.length === 0) return null;
    return (
        <div class='table-responsive mt-2'>
            <table class='table table-sm mb-0'>
                <thead>
                    <tr class='small text-body-secondary'>
                        <th><Text id='convention.economy-col-unit'>Sales unit</Text></th>
                        <th class='text-end'><Text id='convention.economy-col-captures'>Captures</Text></th>
                        <th class='text-end'><Text id='convention.economy-col-gross'>Gross</Text></th>
                        <th class='text-end'><Text id='convention.economy-col-fees'>Fees</Text></th>
                        <th class='text-end'><Text id='convention.economy-col-net'>Net</Text></th>
                        <th class='text-end d-none d-md-table-cell'><Text id='convention.economy-col-payouts'>Payouts</Text></th>
                    </tr>
                </thead>
                <tbody>
                    {units.map((u, idx) => (
                        <tr key={`u-${idx}`}>
                            <td>
                                {u.name}
                                {(u.number || u.paymentSolution) && (
                                    <small class='text-body-secondary ms-1'>
                                        {[u.number, u.paymentSolution].filter(Boolean).join(' · ')}
                                    </small>
                                )}
                            </td>
                            <td class='text-end'>{u.captureCount !== null ? u.captureCount : '—'}</td>
                            <td class='text-end'>{u.gross !== null ? krExact(u.gross) : '—'}</td>
                            <td class='text-end'>{u.fees !== null ? krExact(u.fees) : '—'}</td>
                            <td class='text-end'>{u.net !== null ? krExact(u.net) : '—'}</td>
                            <td class='text-end d-none d-md-table-cell'>{u.payouts !== null ? krExact(u.payouts) : '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function EconomyReportCard({
    report, expanded, onToggle, onDelete, overlaps = [], onToggleInclude,
}) {
    const isOpen = !!expanded;
    const included = report.includeInSum !== false;
    const overlapTitle = overlaps
        .map(o => `${o.filename}${o.periodFrom ? ` (${o.periodFrom} – ${o.periodTo || ''})` : ''}`)
        .join('\n');
    const warnings = Array.isArray(report.warnings) ? report.warnings : [];
    const gross = reportGross(report);
    const totals = report.totals || {};
    const period = [report.periodFrom, report.periodTo].filter(Boolean)
        .map(d => util.isoDate(d, false, false, true)).join(' – ');
    const meta = [
        report.provider,
        report.salesUnit,
        report.vippsNumber ? `Vipps-nr ${report.vippsNumber}` : null,
        period,
    ].filter(Boolean).join(' · ');
    return (
        <div class='card mb-2' key={report.md5}>
            <div class='card-body py-2'>
                <div
                    class='d-flex justify-content-between align-items-center'
                    style='cursor: pointer;'
                    onClick={onToggle}
                >
                    <div class='d-flex flex-column' style='min-width: 0;'>
                        <span class='fw-bold text-truncate'>
                            <i class={`fa-solid me-2 ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                            <span class={`badge ${KIND_CLASS[report.kind] || KIND_CLASS.other} me-2`}>
                                {KIND_LABEL[report.kind] || KIND_LABEL.other}
                            </span>
                            {report.filename}
                        </span>
                        {meta && <small class='text-body-secondary ms-4'>{meta}</small>}
                    </div>
                    <div class='d-flex align-items-center gap-2 ms-2'>
                        {overlaps.length > 0 && (
                            <span class='badge text-bg-danger' title={`Overlapper med:\n${overlapTitle}`}>
                                <i class='fa-solid fa-layer-group me-1' />
                                <Text id='economy.report-overlaps'>Overlaps</Text>
                            </span>
                        )}
                        {!included && (
                            <span class='badge text-bg-secondary' title='Telles ikke i summen'>
                                <Text id='economy.report-excluded'>Not counted</Text>
                            </span>
                        )}
                        {warnings.length > 0 && (
                            <span class='badge text-bg-warning' title={warnings.join('\n')}>
                                <i class='fa-solid fa-triangle-exclamation me-1' />{warnings.length}
                            </span>
                        )}
                        {gross !== null && <span class='fw-bold text-nowrap'>{kr(gross)}</span>}
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-danger rounded-pill'
                            title='Fjern rapporten'
                            onClick={e => { e.stopPropagation(); onDelete(); }}
                        >
                            <i class='fa-solid fa-trash' />
                        </button>
                    </div>
                </div>

                {isOpen && (
                    <div class='mt-2'>
                        {overlaps.length > 0 && (
                            <div class='alert alert-danger py-2 mb-2 small'>
                                <div class='fw-bold'>
                                    <i class='fa-solid fa-layer-group me-1' />
                                    <Text id='economy.report-overlap-title'>Overlapping period</Text>
                                </div>
                                <div>
                                    <Text id='economy.report-overlap-help'>
                                        These reports cover the same sales unit in the same period, so they describe the same money. Only one of them should count in the sum.
                                    </Text>
                                </div>
                                <ul class='mb-0 ps-3'>
                                    {overlaps.map(o => (
                                        <li key={o.md5 || o.id}>
                                            {o.filename}
                                            {o.periodFrom && ` · ${o.periodFrom} – ${o.periodTo || ''}`}
                                            {o.includeInSum === false && ' · '}
                                            {o.includeInSum === false && <Text id='economy.report-excluded'>Not counted</Text>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {onToggleInclude && (
                            <div class='form-check form-switch mb-2'>
                                <input
                                    class='form-check-input'
                                    type='checkbox'
                                    role='switch'
                                    id={`include-${report.md5}`}
                                    checked={included}
                                    onChange={e => onToggleInclude(!!e.target.checked)}
                                />
                                <label class='form-check-label small' for={`include-${report.md5}`}>
                                    <Text id='economy.report-include-in-sum'>Count this report in the sum</Text>
                                </label>
                            </div>
                        )}
                        {warnings.length > 0 && (
                            <div class='alert alert-warning py-2 mb-2 small'>
                                <div class='fw-bold'>
                                    <i class='fa-solid fa-triangle-exclamation me-1' />
                                    <Text id='convention.economy-check-figures'>Check the figures</Text>
                                </div>
                                <ul class='mb-0 ps-3'>
                                    {warnings.map((w, idx) => <li key={`w-${idx}`}>{w}</li>)}
                                </ul>
                            </div>
                        )}
                        <SalesLines report={report} />
                        <SalesUnits report={report} />
                        <div class='d-flex flex-wrap gap-3 mt-2 small'>
                            {totals.incVat !== null && totals.incVat !== undefined && (
                                <span><Text id='convention.economy-total-inc-vat'>Total incl. VAT</Text>: <strong>{krExact(totals.incVat)}</strong></span>
                            )}
                            {totals.vat !== null && totals.vat !== undefined && (
                                <span><Text id='convention.economy-total-vat'>VAT</Text>: <strong>{krExact(totals.vat)}</strong></span>
                            )}
                            {totals.gross !== null && totals.gross !== undefined && (
                                <span><Text id='convention.economy-total-gross'>Gross</Text>: <strong>{krExact(totals.gross)}</strong></span>
                            )}
                            {totals.fees !== null && totals.fees !== undefined && (
                                <span><Text id='convention.economy-total-fees'>Fees</Text>: <strong>{krExact(totals.fees)}</strong></span>
                            )}
                            {totals.net !== null && totals.net !== undefined && (
                                <span><Text id='convention.economy-total-net'>Net</Text>: <strong>{krExact(totals.net)}</strong></span>
                            )}
                        </div>
                        {report.notes && (
                            <div class='small text-body-secondary mt-2'>
                                <i class='fa-regular fa-note-sticky me-1' />{report.notes}
                            </div>
                        )}
                        <div class='small text-body-tertiary mt-2'>
                            {report.uploadedByName}
                            {report.uploadedDate ? ` · ${util.formatDate(report.uploadedDate, {
                                hour12: false, hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short', locale: 'nb-NO',
                            })}` : ''}
                            {report.model ? ` · ${report.model}` : ''}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default EconomyReportCard;
